import React from "react";
import { MapPin, Mail, Phone, Building2, Clock } from "lucide-react";
import { useApp, useUI } from "@/context/AppContext";
import { useCookieConsent } from "@/context/CookieConsent";
import useSEO from "@/hooks/useSEO";
import { PageHero } from "@/components/primitives";
import { Reveal } from "@/components/Reveal";
import BuyerForm from "@/components/BuyerForm";
import ConsultingForm from "@/components/ConsultingForm";

export default function Contact() {
  const { t, lang } = useApp();
  const ui = useUI();
  const { consent, accept } = useCookieConsent();
  useSEO(
    { fr: "Contact | Agrivoire Export SARL", en: "Contact | Agrivoire Export SARL" },
    { fr: "Contactez Agrivoire Export SARL : formulaire acheteur/fournisseur et formulaire partenaire/conseil.", en: "Contact Agrivoire Export SARL: buyer/supplier form and partner/consulting form." }
  );

  return (
    <div data-testid="contact-page">
      <PageHero kicker={t("contact", "hero_kicker")} title={t("contact", "hero_title")} sub={t("contact", "hero_sub")} />

      {/* Contact details */}
      <section className="border-b border-black/5 bg-white py-14" data-testid="contact-details">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <h2 className="mb-8 font-montserrat text-xl font-bold text-neutral-900">{t("contact", "details_title")}</h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {(() => {
              const phone = t("global", "phone");
              const email = t("global", "email");
              const phoneIsPlaceholder = !phone || /x/i.test(phone);
              const items = [
                { icon: Building2, label: ui.label_company, value: t("global", "company_name"), testId: "contact-detail-company" },
                { icon: MapPin, label: ui.label_location, value: t("global", "location"), testId: "contact-detail-location" },
                { icon: Mail, label: ui.label_email, value: email, href: `mailto:${email}`, testId: "contact-detail-email" },
              ];
              if (!phoneIsPlaceholder) {
                items.push({ icon: Phone, label: ui.label_phone, value: phone, href: `tel:${phone.replace(/\s+/g, "")}`, testId: "contact-detail-phone" });
              }
              return items.map((d, i) => {
                const Icon = d.icon;
                const inner = (
                  <>
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#8CC63F]/10 text-[#4E7D2C]">
                      <Icon className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="font-poppins text-xs uppercase tracking-wide text-neutral-400">{d.label}</p>
                      <p className="font-montserrat text-base font-semibold text-neutral-900">{d.value}</p>
                    </div>
                  </>
                );
                return d.href ? (
                  <a key={i} href={d.href} className="flex items-center gap-4 transition-opacity hover:opacity-80" data-testid={d.testId}>
                    {inner}
                  </a>
                ) : (
                  <div key={i} className="flex items-center gap-4" data-testid={d.testId}>{inner}</div>
                );
              });
            })()}
          </div>

          <div className="mt-8 flex items-center gap-3 rounded-2xl border border-[#8CC63F]/20 bg-[#8CC63F]/5 px-5 py-4" data-testid="contact-response-time">
            <Clock className="h-5 w-5 shrink-0 text-[#4E7D2C]" />
            <p className="font-poppins text-sm font-medium text-neutral-700">{t("contact", "response_time")}</p>
          </div>

          <div className="mt-8 overflow-hidden rounded-2xl border border-black/5" data-testid="contact-map">
            {consent === "accepted" ? (
              <iframe
                title="Abidjan, Côte d'Ivoire"
                src="https://www.openstreetmap.org/export/embed.html?bbox=-4.15%2C5.28%2C-3.88%2C5.42&amp;layer=mapnik&amp;marker=5.3599%2C-4.0083"
                className="h-[260px] w-full md:h-[320px]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map-iframe"
              />
            ) : (
              <div className="flex h-[260px] w-full flex-col items-center justify-center gap-3 bg-neutral-100 px-6 text-center md:h-[320px]" data-testid="contact-map-placeholder">
                <MapPin className="h-6 w-6 text-[#4E7D2C]" />
                <p className="max-w-sm font-poppins text-sm text-neutral-600">
                  {lang === "fr"
                    ? "La carte est fournie par OpenStreetMap (service tiers). Autorisez les cookies tiers pour l'afficher."
                    : "The map is provided by OpenStreetMap (third-party service). Allow third-party cookies to display it."}
                </p>
                <button
                  onClick={accept}
                  data-testid="contact-map-consent-btn"
                  className="rounded-full bg-[#8CC63F] px-5 py-2.5 font-poppins text-sm font-semibold text-[#0A0A0A] transition-transform hover:scale-105"
                >
                  {lang === "fr" ? "Afficher la carte" : "Show the map"}
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Two forms */}
      <section className="mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-28">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <div id="buyer-form" className="scroll-mt-24" data-testid="contact-buyer-block">
              <div className="mb-8">
                <span className="block h-1.5 w-12 rounded-full bg-gradient-to-r from-[#8CC63F] to-[#4E7D2C]" />
                <h2 className="mt-4 font-montserrat text-2xl font-bold text-neutral-900 sm:text-3xl">{t("contact", "buyer_section_title")}</h2>
                <p className="mt-2 font-poppins text-base text-neutral-600">{t("contact", "buyer_section_desc")}</p>
              </div>
              <BuyerForm />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div id="consulting-form" className="scroll-mt-24" data-testid="contact-consulting-block">
              <div className="mb-8">
                <span className="block h-1.5 w-12 rounded-full bg-[#1CADE4]" />
                <h2 className="mt-4 font-montserrat text-2xl font-bold text-neutral-900 sm:text-3xl">{t("contact", "consulting_section_title")}</h2>
                <p className="mt-2 font-poppins text-base text-neutral-600">{t("contact", "consulting_section_desc")}</p>
              </div>
              <ConsultingForm />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
